import { Component, DestroyRef, inject, TemplateRef, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PanelComponent } from '@components/core-template/panel/panel.component';
import { ReviewRequestComponent } from '@components/requests/review-request/review-request.component';
import { NumberPipe } from 'app/pipes/number.pipe';
import { MicrosoftAuthService } from '@services/core/authentication.service';
import { Global } from '@services/core/Global';
import Swal from 'sweetalert2'

interface FieldDiff {
  field: string
  excel: any
  db: any
}

interface CompareRow {
  rut: number
  nombreCompleto: string | null
  edad: number | null
  inicioVigencia: string | null
  finVigencia: string | null
  totalSura: number | null
  totalIasd: number | null
  ultimoMovimiento: string | null
  requestId: string | null
  type: 'new' | 'missing' | 'modified'
  differences: FieldDiff[]
}

interface CompareResult {
  summary: {
    totalExcel: number
    totalDB: number
    new: number
    modified: number
    missing: number
    equal: number
    errors: number
  }
  policy: { code: string; name: string }
  period: string
  differences: CompareRow[]
  errors: { row: number; message: string }[]
}

@Component({
  selector: 'app-compare-payroll',
  standalone: true,
  imports: [CommonModule, FormsModule, PanelComponent, ReviewRequestComponent, NumberPipe],
  templateUrl: './compare-payroll.component.html',
  styleUrls: ['./compare-payroll.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComparePayrollComponent {
  private _http = inject(HttpClient)
  private _authService = inject(MicrosoftAuthService)
  private _modalService = inject(NgbModal)

  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)

  @ViewChild('detailModal', { static: true }) detailModal!: TemplateRef<any>
  @ViewChild('requestModal', { static: true }) requestModal!: TemplateRef<any>

  protected filesToUpload: File[] | null = null
  protected fileName: string | null = null
  protected loading = false
  protected result: CompareResult | null = null
  protected filterType: string = 'all'
  protected selectedDiff: CompareRow | null = null
  protected selectedRequestId: string | null = null

  protected Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true,
  })

  fileChangeEvent(fileInput: any) {
    this.result = null
    const files = fileInput.target.files
    if (files.length === 1) {
      this.filesToUpload = [files[0]]
      this.fileName = files[0].name
    } else {
      this.Toast.fire({ title: 'Seleccione un archivo .xlsx', icon: 'info' })
    }
  }

  clearFile() {
    this.filesToUpload = null
    this.fileName = null
    this.result = null
  }

  compareFile() {
    if (!this.filesToUpload || this.filesToUpload.length === 0) {
      this.Toast.fire({ title: 'Seleccione un archivo Excel', icon: 'info' })
      return
    }

    this.loading = true
    this.result = null

    Swal.fire({
      text: 'Comparando archivo con registros internos...',
      didOpen: () => Swal.showLoading(),
    })

    const formData = new FormData()
    formData.append('files', this.filesToUpload[0], this.filesToUpload[0].name)

    this._http.post<CompareResult>(Global.url_seguros + 'upload-file/payrolls/compare', formData, {
      headers: {
        'Authorization': this._authService.getToken(),
        'Accept': 'application/json; charset=utf-8'
      }
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        Swal.close()
        this.loading = false
        this.result = res
        const s = res.summary
        const msg = `Excel: ${s.totalExcel} registros | BD: ${s.totalDB} registros`
        this.Toast.fire({ title: msg, icon: 'info' })
        this.cdr.markForCheck()
      },
      error: (err) => {
        Swal.close()
        this.loading = false
        this.cdr.markForCheck()
        const msg = err.error?.message || 'Error al comparar archivo'
        this.Toast.fire({ title: msg, icon: 'error' })
      }
    })
  }

  openDetailModal(diff: CompareRow) {
    this.selectedDiff = diff
    this._modalService.open(this.detailModal, { size: 'lg', scrollable: true })
  }

  openRequestModal(requestId: string) {
    this.selectedRequestId = requestId
    this._modalService.open(this.requestModal, { size: 'xl', scrollable: true })
  }

  get filteredDifferences(): CompareRow[] {
    if (!this.result) return []
    if (this.filterType === 'all') return this.result.differences
    return this.result.differences.filter(d => d.type === this.filterType)
  }

  get detailFields(): FieldDiff[] {
    if (!this.selectedDiff) return []
    return this.selectedDiff.differences.filter(f => {
      const excelFalsy = f.excel == null || f.excel === 0 || f.excel === '0' || f.excel === ''
      const dbFalsy = f.db == null || f.db === 0 || f.db === '0' || f.db === ''
      return !(excelFalsy && dbFalsy)
    })
  }

  typeBadgeClass(type: string): string {
    switch (type) {
      case 'new': return 'bg-success'
      case 'missing': return 'bg-danger'
      case 'modified': return 'bg-warning text-dark'
      default: return 'bg-secondary'
    }
  }

  typeLabel(type: string): string {
    switch (type) {
      case 'new': return 'Nuevo en Excel'
      case 'missing': return 'Falta en Excel'
      case 'modified': return 'Modificado'
      default: return type
    }
  }
}
